import React, { useEffect, useState, useCallback } from 'react';
import { Offcanvas, Button as BsButton } from 'react-bootstrap';
import { MDBTable, MDBTableHead, MDBTableBody, MDBBtn } from 'mdb-react-ui-kit';
import { getInfluencers, updateShortlist } from '../services/api';
import InstagramSvg from '../assets/icons/instagram.svg';
import TikTokSvg from '../assets/icons/tiktok-icon.svg';

const ShortlistDetailsDrawer = ({ show, onClose, shortlist }) => {
    const [influencers, setInfluencers] = useState([]);

    // Utility: Format follower count
    const formatFollowers = useCallback(count => {
        const formats = [
            { threshold: 1e9, suffix: 'B' },
            { threshold: 1e6, suffix: 'M' },
            { threshold: 1e3, suffix: 'K' }
        ];
        const format = formats.find(f => count >= f.threshold);
        if (!format) return count.toString();
        return (count / format.threshold).toFixed(1).replace(/\.0$/, '') + format.suffix;
    }, []);

    useEffect(() => {
        const enrichInfluencerData = (influencer) => {
            const niches = influencer.niche ? influencer.niche.split(',').map(n => n.trim()) : [];
            const platform = influencer.instagramUsername && influencer.tikTokUsername
                ? 'Instagram & TikTok'
                : influencer.instagramUsername ? 'Instagram' : 'TikTok';

            const enrichedInfluencer = { ...influencer, niches, platform };

            if (influencer.workPlatformName === 'Instagram') {
                enrichedInfluencer.IG_followers = influencer.followers;
                enrichedInfluencer.IG_engagementRate = influencer.engagementRate * 100;
                enrichedInfluencer.IG_avgLikes = influencer.averageLikes;
                enrichedInfluencer.IG_avgComments = influencer.averageComments;
            } else if (influencer.workPlatformName === 'TikTok') {
                enrichedInfluencer.TT_followers = influencer.followers;
                enrichedInfluencer.TT_engagementRate = influencer.engagementRate * 100;
                enrichedInfluencer.TT_avgLikes = influencer.averageLikes;
                enrichedInfluencer.TT_avgComments = influencer.averageComments;
            }

            return enrichedInfluencer;
        };

        const mergeInfluencerData = (data) => {
            return Object.values(data.reduce((acc, curr) => {
                if (!acc[curr.influencerId]) {
                    acc[curr.influencerId] = curr;
                } else {
                    acc[curr.influencerId] = {
                        ...acc[curr.influencerId],
                        ...curr,
                        IG_followers: acc[curr.influencerId].IG_followers || curr.IG_followers,
                        IG_engagementRate: acc[curr.influencerId].IG_engagementRate || curr.IG_engagementRate,
                        IG_avgLikes: acc[curr.influencerId].IG_avgLikes || curr.IG_avgLikes,
                        IG_avgComments: acc[curr.influencerId].IG_avgComments || curr.IG_avgComments,
                        TT_followers: acc[curr.influencerId].TT_followers || curr.TT_followers,
                        TT_engagementRate: acc[curr.influencerId].TT_engagementRate || curr.TT_engagementRate,
                        TT_avgLikes: acc[curr.influencerId].TT_avgLikes || curr.TT_avgLikes,
                        TT_avgComments: acc[curr.influencerId].TT_avgComments || curr.TT_avgComments
                    };
                }
                return acc;
            }, {}));
        };

        const fetchInfluencers = async () => {
            if (shortlist?.influencerIds) {
                try {
                    const influencerIds = shortlist.influencerIds.split(',').map(id => parseInt(id, 10));
                    const params = new URLSearchParams();
                    influencerIds.forEach(id => params.append("influencerIds", id));
                    const { data } = await getInfluencers(params);

                    const enrichedData = data.map(enrichInfluencerData);
                    const mergedData = mergeInfluencerData(enrichedData);

                    setInfluencers(mergedData);
                } catch (error) {
                    console.error('Error fetching influencers:', error);
                    setInfluencers([]);
                }
            }
        };

        if (show) fetchInfluencers();
    }, [shortlist, show]);

    const handleRowClick = influencer => {
        console.log("influencer", influencer);
        localStorage.setItem(`selectedInfluencer${influencer.influencerId}`, JSON.stringify(influencer));
        window.open(`/details?id=${influencer.influencerId}`, '_blank');
    };

    const handleRemoveClick = async (influencer) => {
        if (!shortlist || !shortlist.influencerIds) return;
    
        const updatedInfluencerIds = shortlist.influencerIds
            .split(',')
            .map(id => id.trim())
            .filter(id => id !== influencer.influencerId.toString())
            .join(',');
    
        try {
            const updatedShortlist = { ...shortlist, influencerIds: updatedInfluencerIds };
            await updateShortlist(updatedShortlist);
    
            setInfluencers(prevInfluencers => prevInfluencers.filter(i => i.influencerId !== influencer.influencerId));
        } catch (error) {
            console.error('Error removing influencer from shortlist:', error);
        }
    };

    // Function to export CSV
    const handleExportCSV = () => {
        if (!influencers.length) {
            alert("No influencers to export.");
            return;
        }

        // Define CSV headers
        const headers = ["InfluencerId", "CreatorName", "Email", "Platform", "IG_followers", "TT_followers"];
        const csvRows = [];

        // Add header row
        csvRows.push(headers.join(","));

        // Add rows for each influencer
        influencers.forEach(influencer => {
            const row = [
                influencer.influencerId,
                `"${influencer.creatorName}"`,
                `"${influencer.email}"`,
                `"${influencer.platform}"`,
                influencer.IG_followers || 0,
                influencer.TT_followers || 0
            ];
            csvRows.push(row.join(","));
        });

        // Convert rows array to CSV string
        const csvString = csvRows.join("\n");

        // Create a blob and trigger the download
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "shortlist.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Offcanvas show={show} placement="end" onHide={onClose} className='p-3 w-75'>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>{shortlist?.name} - Shortlist</Offcanvas.Title>
                {/* Export button added in the header */}
                <BsButton variant="secondary" onClick={handleExportCSV} className="ms-3">
                    Export
                </BsButton>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {influencers.length > 0 ? (
                    <MDBTable align='left' striped hover responsive>
                        <MDBTableHead>
                            <tr style={{ backgroundColor: '#f0f0f0', color: '#7d7d7d' }}>
                                <th>#</th>
                                <th>Influencer</th>
                                <th>Platform</th>
                                <th>Followers</th>
                                <th>Actions</th>
                            </tr>
                        </MDBTableHead>
                        <MDBTableBody>
                            {influencers.map((influencer, index) => (
                                <tr key={influencer.influencerId}>
                                    <td>{index + 1}</td>
                                    <td className='align-items-center'>
                                        <div style={{ display: 'flex' }}>
                                            <img
                                                src={influencer.profileImg || 'https://mdbcdn.b-cdn.net/img/Photos/new-templates/bootstrap-profiles/avatar-2.webp'}
                                                alt='profile'
                                                style={{ width: '40px', height: '40px' }}
                                                className='rounded-circle me-2'
                                            />
                                            <div>
                                                <p className='fw-bold mb-0'>{influencer.creatorName}</p>
                                                <small className='text-muted'>{influencer.email}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <>
                                            {influencer.platform === 'Instagram' && (
                                                <a
                                                    href={`https://instagram.com/${influencer.instagramUsername.replace('@', '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <img
                                                        src={InstagramSvg}
                                                        alt="Instagram"
                                                        style={{ width: '20px', height: '20px', marginRight: '5px' }}
                                                    />
                                                    {influencer.instagramUsername}
                                                </a>
                                            )}
                                            {influencer.platform === 'TikTok' && (
                                                <a
                                                    href={`https://tiktok.com/${influencer.tikTokUsername.startsWith('@') ? influencer.tikTokUsername : `@${influencer.tikTokUsername}`}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <img
                                                        src={TikTokSvg}
                                                        alt="TikTok"
                                                        style={{ width: '20px', height: '20px', marginRight: '5px' }}
                                                    />
                                                    {influencer.tikTokUsername}
                                                </a>
                                            )}
                                            {influencer.platform === 'Instagram & TikTok' && (
                                                <>
                                                    <a
                                                        href={`https://instagram.com/${influencer.instagramUsername.replace('@', '')}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img
                                                            src={InstagramSvg}
                                                            className="mx-1 my-1"
                                                            alt="Instagram"
                                                            style={{ width: '20px', height: '20px' }}
                                                        />
                                                        {influencer.instagramUsername}
                                                    </a>
                                                    <br />
                                                    <a
                                                        href={`https://tiktok.com/${influencer.tikTokUsername.startsWith('@') ? influencer.tikTokUsername : `@${influencer.tikTokUsername}`}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img
                                                            src={TikTokSvg}
                                                            alt="TikTok"
                                                            className="mx-1 my-1"
                                                            style={{ width: '20px', height: '20px' }}
                                                        />
                                                        {influencer.tikTokUsername}
                                                    </a>
                                                </>
                                            )}
                                        </>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex' }}>
                                            <img src={InstagramSvg} alt="Instagram" className='my-1' style={{ width: '20px', height: '20px', marginRight: '2px' }} /> {formatFollowers(influencer?.IG_followers || 0)}
                                        </div>
                                        <div style={{ display: 'flex' }}>
                                            <img src={TikTokSvg} alt="TikTok" className='my-1' style={{ width: '20px', height: '20px', marginRight: '2px' }} /> {formatFollowers(influencer?.TT_followers || 0)}
                                        </div>
                                    </td>
                                    <td>
                                        <MDBBtn color='link' className='p-1' onClick={() => handleRowClick(influencer)}>View</MDBBtn>
                                        <MDBBtn color='link' className='p-1 text-danger' onClick={() => handleRemoveClick(influencer)}>Remove</MDBBtn>
                                    </td>
                                </tr>
                            ))}
                        </MDBTableBody>
                    </MDBTable>
                ) : (
                    <p>No influencers in this shortlist.</p>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default ShortlistDetailsDrawer;
